import { Dispatch, SetStateAction } from 'react';
import { ace_index } from '@axo/shared/data-access/types';
import { FilterSelect } from '../FilterSelect';
import { areObjectsDifferent } from '../../utils/compareObjects';
import { setPageNumber } from '../SearchResults';

interface Props {
  query: ace_index.FindInput;
  setQuery: Dispatch<SetStateAction<ace_index.FindInput>>;
  editable?: boolean;
}

export function ApplicationUpdatedAfter({
  query,
  setQuery,
  editable = false,
}: Props) {
  const state = getApplicationUpdatedAfter(query);
  const value =
    options.find((option) => !areObjectsDifferent(state, option.value)) ??
    options[0];

  return (
    <FilterSelect
      items={options}
      onSelect={(option) => setQuery(setApplicationUpdatedAfter(option.value))}
      stringify={(option) => option.label}
      value={value}
      label="Application update time"
      description="Filter when the application was last updated"
      disabled={!editable}
    />
  );
}

export function ApplicationOrQuotesUpdatedAfter({
  query,
  setQuery,
  editable = false,
}: Props) {
  const state = getApplicationOrQuotesUpdatedAfter(query);
  const value =
    options.find((option) => !areObjectsDifferent(state, option.value)) ??
    options[0];

  return (
    <FilterSelect
      items={options}
      onSelect={(option) =>
        setQuery(setApplicationOrQuotesUpdatedAfter(option.value))
      }
      stringify={(option) => option.label}
      value={value}
      label="Application or quotes update time"
      description="Filter when the application or quotes were last updated"
      disabled={!editable}
    />
  );
}

const options: { label: string; value: Value }[] = [
  { label: 'All', value: undefined },
  { label: 'Last day', value: { From: days(-1), FromStartOfDay: true } },
  { label: 'Last 2 days', value: { From: days(-2), FromStartOfDay: true } },
  { label: 'Last 7 days', value: { From: days(-7), FromStartOfDay: true } },
  { label: 'Last 18 days', value: { From: days(-18), FromStartOfDay: true } },
  { label: 'Last 30 days', value: { From: days(-30), FromStartOfDay: true } },
  { label: 'Last 60 days', value: { From: days(-60), FromStartOfDay: true } },
  { label: 'Last 90 days', value: { From: days(-90), FromStartOfDay: true } },
];

function days(days: number) {
  return days * 24 * 60;
}

function setApplicationUpdatedAfter(
  offset: Value,
): SetStateAction<ace_index.FindInput> {
  return (query) => ({
    ...setPageNumber(1)(query),
    LoanApplicationUpdatedAtOffset: offset,
  });
}

function setApplicationOrQuotesUpdatedAfter(
  offset: Value,
): SetStateAction<ace_index.FindInput> {
  return (query) => ({
    ...setPageNumber(1)(query),
    SummaryLastUpdateAtOffset: offset,
  });
}

function getApplicationUpdatedAfter(query: ace_index.FindInput) {
  return query.LoanApplicationUpdatedAtOffset;
}

function getApplicationOrQuotesUpdatedAfter(query: ace_index.FindInput) {
  return query.SummaryLastUpdateAtOffset;
}

type Value = ace_index.FindInput['LoanApplicationUpdatedAtOffset'] &
  ace_index.FindInput['SummaryLastUpdateAtOffset'];
